import { useMemo, useState } from "react"
import { Link } from "@tanstack/react-router"
import { IconSquareArrowUpRightOutline18 as ExternalLink } from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import { useDarkMode } from "@productlane/ui"

import { env, isDevEnvironment } from "@/env"
import { getPortalBaseUrl } from "@/lib/portal-base-url"

interface Workspace {
	domain: string
	customDomain: string | null
	changelogPublic?: boolean
	helpCenterPublic?: boolean
	domainPublic?: boolean
	customerPortalSettings?: { isEnabled?: boolean } | null
}

interface Props {
	workspace: Workspace | null | undefined
	settingsHash: string
	hasAnyPortalEnabled: boolean
	emptyMessage?: string
}

function getFirstAvailablePortalPath(workspace: Workspace): string {
	if (workspace.changelogPublic) return "/changelog"
	if (workspace.helpCenterPublic) return "/docs"
	if (workspace.domainPublic) return "/request"
	if (workspace.customerPortalSettings?.isEnabled) return "/support"
	return "/changelog"
}

// Render the iframe at a real desktop viewport (1280 px) and scale it down
// to fit the preview column. Mintlify-style: admins see the real desktop
// layout shrunk to fit, instead of the portal's mobile breakpoint.
const VIEWPORT_WIDTH = 1280
const PREVIEW_WIDTH = 422

export function PortalPreview({
	workspace,
	settingsHash,
	hasAnyPortalEnabled,
	emptyMessage = "Enable Changelog, Docs, Requests or Support Portal to see a preview.",
}: Props) {
	const { isDarkMode } = useDarkMode()
	const [debouncedHash, setDebouncedHash] = useState(settingsHash)

	useDebounce(() => setDebouncedHash(settingsHash), 500, [settingsHash])

	const { src, href } = useMemo(() => {
		if (!workspace || !hasAnyPortalEnabled) {
			return { src: null, href: null }
		}
		// In dev, the workspace's prod subdomain (acme.productlane.com) doesn't
		// exist locally, so route the preview through the local apps/web
		// instance (VITE_API_URL → http://localhost:3000) which always resolves
		// to the "hello" dev workspace.
		const base = (
			isDevEnvironment ? env.VITE_API_URL : getPortalBaseUrl(workspace)
		).replace(/\/$/, "")
		const path = getFirstAvailablePortalPath(workspace)
		const params = new URLSearchParams({
			mode: isDarkMode ? "dark" : "light",
			rev: debouncedHash,
		})
		return {
			src: `${base}/portal-preview${path}?${params.toString()}`,
			href: `${base}${path}`,
		}
	}, [workspace, isDarkMode, debouncedHash, hasAnyPortalEnabled])

	const containerHeight = 290
	const padding = 12
	const innerWidth = PREVIEW_WIDTH - padding * 2
	const innerHeight = containerHeight - padding * 2
	// Scale the iframe so its visible footprint matches the inner (padded)
	// wrapper exactly. Without this the iframe would overflow the mat.
	const innerScale = innerWidth / VIEWPORT_WIDTH
	const iframeHeight = Math.round(innerHeight / innerScale)

	const frame = (
		<div
			className="relative rounded-xl border border-gray-200 bg-gray-100 dark:border-gray-800 dark:bg-gray-900"
			style={{
				width: PREVIEW_WIDTH,
				height: containerHeight,
				padding,
			}}
		>
			{src ? (
				<div
					className="relative overflow-hidden rounded-md"
					style={{ width: innerWidth, height: innerHeight }}
				>
					<iframe
						key={debouncedHash}
						src={src}
						title="Portal preview"
						className="border-0"
						sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
						style={{
							width: VIEWPORT_WIDTH,
							height: iframeHeight,
							transform: `scale(${innerScale})`,
							transformOrigin: "top left",
							pointerEvents: "none",
						}}
					/>
					<div className="absolute inset-0 bg-white/0 transition-colors duration-150 group-hover/preview:bg-white/10" />
				</div>
			) : (
				<div className="flex h-full items-center justify-center p-6 text-center text-sm text-secondary">
					{emptyMessage}
				</div>
			)}
		</div>
	)

	const hostname = workspace
		? workspace.customDomain
			? workspace.customDomain
			: `${workspace.domain}.productlane.com`
		: null

	return (
		<div className="sticky top-8 hidden lg:block">
			{href ? (
				<a
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					className="group/preview block transition-shadow hover:shadow-lg"
				>
					{frame}
				</a>
			) : (
				frame
			)}
			{hostname && (
				<div
					className="mt-3 flex items-center justify-between text-xs text-secondary"
					style={{ width: PREVIEW_WIDTH }}
				>
					{href ? (
						<a
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							className="truncate hover:text-primary hover:underline"
						>
							{hostname}
						</a>
					) : (
						<span className="truncate">{hostname}</span>
					)}
					{!workspace?.customDomain && (
						<Link
							to="/settings/portal/domain"
							className="ml-2 inline-flex flex-shrink-0 items-center gap-1 text-primary hover:underline"
						>
							Add custom domain
							<ExternalLink className="h-3 w-3" />
						</Link>
					)}
				</div>
			)}
		</div>
	)
}
